/**
 * The shops.
 *
 * WHY THE SWITCH LEADS THE SCREEN
 * -------------------------------
 * A branch that has stopped taking orders is the thing somebody needs to see
 * before anything else on this page — it means a shop is not trading right
 * now. Everything else here is a form somebody fills in once a year.
 *
 * WHY PAUSING ASKS WHY
 * --------------------
 * Somebody will want to know afterwards why the shop was shut for forty
 * minutes on Tuesday, and the person who knows is the one turning it off. It
 * is not insisted on — a power cut does not wait for typing — but it is asked
 * for, and it goes into the log beside the pause.
 *
 * WHY "closed down" AND "not taking orders" ARE DIFFERENT CONTROLS
 * ---------------------------------------------------------------
 * One is a decision about the business, the other is Tuesday evening.
 * Collapsing them would mean the only way to pause a rush is to shut the shop.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';
import MapPin from '../../components/MapPin.jsx';

const TYPES = {
  restaurant: 'Restaurant',
  cloud_kitchen: 'Cloud kitchen',
  central_kitchen: 'Central kitchen',
  warehouse: 'Warehouse',
};

export default function BranchesPage() {
  const [editing, setEditing] = useState(null);
  const [pausing, setPausing] = useState(null);
  const queryClient = useQueryClient();

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => api.get('/admin/branches?all=1'),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['branches'] });

  const rows = branches.data?.branches ?? [];
  const paused = branches.data?.pausedCount ?? 0;

  return (
    <div className="mx-auto max-w-4xl p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-semibold text-xl text-ink">Branches</h1>
          <p className="mt-0.5 text-sm text-ink-2">The shops, and whether they are trading.</p>
        </div>
        {!editing && <Button onClick={() => setEditing({})}>Open a branch</Button>}
      </div>

      {/* First thing on the page, because it means a shop is not trading. */}
      {paused > 0 && (
        <p className="mb-4 rounded-lg bg-warn-bg px-3.5 py-2.5 text-sm text-warn">
          {paused === 1 ? 'A branch has' : `${paused} branches have`} stopped taking new orders.
          Orders already placed can still be finished.
        </p>
      )}

      {editing && (
        <BranchForm
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            refresh();
            setEditing(null);
          }}
        />
      )}

      {pausing && (
        <PausePanel
          branch={pausing}
          onClose={() => setPausing(null)}
          onDone={() => {
            refresh();
            setPausing(null);
          }}
        />
      )}

      {branches.isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner label="Loading" />
        </div>
      ) : branches.isError ? (
        <p className="py-12 text-center text-sm text-ink-2">
          Could not load these. Check the branch computer is running.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((branch) => (
            <div key={branch.id} className="rounded-xl border border-rule bg-surface p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div className="min-w-0">
                  <span className="text-sm text-ink">{branch.name}</span>
                  <span className="ml-2 text-xs tabular-nums text-ink-3">{branch.code}</span>
                  <span className="block text-xs text-ink-3">
                    {TYPES[branch.type] ?? branch.type} · {branch.city} · {branch.opening_time}–
                    {branch.closing_time}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <StatusPill branch={branch} />

                  {branch.is_active === 1 && (
                    <Button variant="secondary" onClick={() => setPausing(branch)}>
                      {branch.is_accepting_orders ? 'Stop orders' : 'Take orders again'}
                    </Button>
                  )}

                  <button
                    type="button"
                    onClick={() => setEditing(branch)}
                    className="text-sm text-ink-2 underline hover:text-ink"
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Trading, paused, or closed down — three states, not two. */
export function StatusPill({ branch }) {
  const state = !branch.is_active
    ? { label: 'Closed down', tone: 'text-ink-3 border-rule' }
    : branch.is_accepting_orders
      ? { label: 'Trading', tone: 'text-brand-400 border-brand-400' }
      : { label: 'Not taking orders', tone: 'text-warn border-warn' };

  return <span className={`rounded border px-2 py-0.5 text-xs ${state.tone}`}>{state.label}</span>;
}

function PausePanel({ branch, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const stopping = branch.is_accepting_orders === 1;

  const save = useMutation({
    mutationFn: () =>
      api.put(`/admin/branches/${branch.id}`, {
        isAcceptingOrders: !stopping,
        reason: reason.trim() || undefined,
      }),
    onSuccess: onDone,
  });

  return (
    <div className="mb-4 rounded-xl border border-rule bg-surface p-5">
      <h2 className="font-medium text-ink">
        {stopping ? `Stop ${branch.name} taking orders` : `${branch.name} takes orders again`}
      </h2>

      {stopping && (
        <>
          <p className="mt-1 text-sm text-ink-2">
            Orders already placed can still be paid for, cooked and sent out. Only starting a new
            one is refused.
          </p>

          <label className="mt-3 block">
            <span className="mb-1.5 block text-xs text-ink-3">
              Why — so somebody can find out later
            </span>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Power cut"
              aria-label="Why"
              className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none"
            />
          </label>
        </>
      )}

      {save.isError && (
        <p className="mt-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {save.error?.message ?? 'That did not work.'}
        </p>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        {/* Not insisted on — a power cut does not wait for typing. */}
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {stopping ? 'Stop taking orders' : 'Take orders again'}
        </Button>
      </div>
    </div>
  );
}

function BranchForm({ initial, onClose, onSaved }) {
  const isNew = !initial.id;

  const [name, setName] = useState(initial.name ?? '');
  const [code, setCode] = useState(initial.code ?? '');
  const [type, setType] = useState(initial.type ?? 'restaurant');
  const [city, setCity] = useState(initial.city ?? '');
  const [address, setAddress] = useState(initial.address ?? '');
  const [phone, setPhone] = useState(initial.phone ?? '');
  const [opening, setOpening] = useState(initial.opening_time ?? '09:00');
  const [closing, setClosing] = useState(initial.closing_time ?? '23:00');
  const [active, setActive] = useState(
    initial.is_active === undefined ? true : !!initial.is_active
  );

  /**
   * Where the shop is.
   *
   * The columns have existed since the first migration and every branch has
   * had them empty, which is why nothing could say how far a delivery was
   * going — there was no point to measure from.
   */
  const [latitude, setLatitude] = useState(initial.latitude ?? null);
  const [longitude, setLongitude] = useState(initial.longitude ?? null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        type,
        city: city.trim(),
        address: address.trim(),
        phone: phone.trim() || null,
        openingTime: opening,
        closingTime: closing,
        isActive: active,
        ...(latitude != null ? { latitude, longitude } : {}),
      };

      return isNew
        ? api.post('/admin/branches', { ...body, code: code.trim().toUpperCase() })
        : api.put(`/admin/branches/${initial.id}`, body);
    },
    onSuccess: onSaved,
  });

  const ready = name.trim() && city.trim() && address.trim() && (!isNew || code.trim());

  const field =
    'h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none';

  return (
    <div className="mb-4 rounded-xl border border-rule bg-surface p-5">
      <h2 className="mb-3 font-medium text-ink">{isNew ? 'Open a branch' : name}</h2>

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="sm:col-span-2">
          <span className="mb-1.5 block text-xs text-ink-3">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={field} />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">
            Short code {!isNew && <span className="text-ink-3">— fixed once set</span>}
          </span>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            disabled={!isNew}
            placeholder="FR-002"
            aria-label="Short code"
            className={`${field} font-mono uppercase disabled:opacity-50`}
          />
          {/* Every order number is built from it, so changing it later would
              orphan every document the branch has ever issued. */}
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Kind</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            aria-label="Kind"
            className={field}
          >
            {Object.entries(TYPES).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">City</span>
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            aria-label="City"
            className={field}
          />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Phone</span>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            aria-label="Phone"
            inputMode="tel"
            className={field}
          />
        </label>

        <label className="sm:col-span-3">
          <span className="mb-1.5 block text-xs text-ink-3">Address</span>
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            aria-label="Address"
            className={field}
          />
        </label>

        {/*
          The shop's own position, which nothing has ever had. Without it a
          delivery has an end and no beginning, so no distance can be worked
          out and no zone can be assigned by how far away somebody is.
        */}
        <div className="sm:col-span-3">
          <span className="mb-1.5 block text-xs text-ink-3">Where the shop is</span>
          <MapPin
            latitude={latitude}
            longitude={longitude}
            onChange={({ latitude: lat, longitude: lng }) => {
              setLatitude(lat);
              setLongitude(lng);
            }}
          />
        </div>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Opens</span>
          <input
            type="time"
            value={opening}
            onChange={(e) => setOpening(e.target.value)}
            aria-label="Opens"
            className={field}
          />
        </label>

        <label>
          <span className="mb-1.5 block text-xs text-ink-3">Closes</span>
          <input
            type="time"
            value={closing}
            onChange={(e) => setClosing(e.target.value)}
            aria-label="Closes"
            className={field}
          />
        </label>
      </div>

      <p className="mt-2 text-xs text-ink-3">
        {/* Stated, because somebody will expect the hours to stop orders. */}
        The hours are what a listing shows. Orders are stopped with the switch on the branch, not by
        the clock — a kitchen trading past midnight is ordinary.
      </p>

      {!isNew && (
        <label className="mt-3 flex items-center gap-2">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="h-5 w-5 rounded border-rule accent-brand-400"
          />
          <span className="text-sm text-ink-2">Still open as a business</span>
        </label>
      )}

      {save.isError && (
        <div className="mt-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {save.error?.details?.length ? (
            <ul className="space-y-0.5">
              {save.error.details.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          ) : (
            (save.error?.message ?? 'That did not work.')
          )}
        </div>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => save.mutate()} disabled={!ready || save.isPending}>
          {save.isPending ? 'Saving' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
